import type {
    RegimeDependenceProfileJob,
    RegimeDependenceProfileReport,
    RegimeDependenceProfileReportSummary,
    RegimeModelVersionOverrides,
    RegimeProfileActiveModelVersionIds,
    RegimeProfileDimensionKey,
    RegimeProfileDimensionResult,
    RegimeProfileSliceMetrics,
    RegimeProfileThresholds,
    RegimeProfileVerdictResult
} from '@/types/appRouter';

/**
 * Pure presentation logic for the Regime Dependence Profile tab
 * (docs/plans/REGIME_DEPENDENCE_PROFILE_FRONTEND_PLAN.md, Surface 1).
 *
 * Every threshold used here is read from the report's own echoed `thresholds` block — the report is a
 * versioned research artifact and two reports may legitimately carry different constants, so nothing in
 * this module hard-codes a gate value.
 */

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatInt(value: number): string {
    return value.toLocaleString('en-US');
}

/** "+0.42%" / "-51.40%". Signed so adverse and favorable states read at a glance. */
export function formatSignedPercent(value: number, fractionDigits = 2): string {
    const formatted = Math.abs(value).toFixed(fractionDigits);
    return `${value < 0 ? '-' : '+'}${formatted}%`;
}

/** 0.87 → "87%" (whole-percent share display). */
export function formatShare(share: number, fractionDigits = 0): string {
    return `${(share * 100).toFixed(fractionDigits)}%`;
}

// ---------------------------------------------------------------------------
// Layer 1 — verdict presentation
// ---------------------------------------------------------------------------

export type RegimeVerdictTone = 'success' | 'warning' | 'danger' | 'neutral';

export interface RegimeVerdictPresentation {
    label: string;
    tone: RegimeVerdictTone;
    gloss: string;
}

/** Distinct adverse primary states named by the verdict's reasons. */
export function countAdverseStates(verdict: RegimeProfileVerdictResult): number {
    const states = new Set<string>();

    for (const reason of verdict.reasons) {
        if (reason.code === 'adverse_regime_state_detected' && reason.state !== undefined) {
            states.add(reason.state);
        }
    }

    return states.size;
}

export function getVerdictPresentation(verdict: RegimeProfileVerdictResult): RegimeVerdictPresentation {
    switch (verdict.verdict) {
        case 'regime_robust':
            return { gloss: 'The edge holds across market states.', label: 'Regime robust', tone: 'success' };
        case 'regime_conditional': {
            const adverseCount = countAdverseStates(verdict);
            const stateNoun = adverseCount === 1 ? 'market state' : 'market states';

            return {
                gloss: `The edge concentrates outside ${adverseCount} ${stateNoun}.`,
                label: 'Regime conditional',
                tone: 'warning'
            };
        }
        case 'regime_fragile_concentrated':
            return {
                gloss: 'The edge rests on too few independent episodes / entities / dates to trust.',
                label: 'Regime fragile / concentrated',
                tone: 'danger'
            };
        case 'insufficient_evidence':
            return { gloss: 'Not enough data to judge.', label: 'Insufficient evidence', tone: 'neutral' };
    }
}

// ---------------------------------------------------------------------------
// Layer 1 — relative-edge chip (never rendered apart from the verdict; design rule 2)
// ---------------------------------------------------------------------------

export type RelativeEdgeDisqualification = 'mixed_instrument_basis' | 'not_positive' | 'coverage_insufficient';

export type RelativeEdgeChipState =
    /**
     * edgePercent is null — zero covered baseline buckets. Unknown, never zero. When the population was
     * also mixed-instrument, the mixed-basis disqualification wins (backend precedence: mixed is judged
     * before zero coverage), so the chip names the reason a rerun with coverage would still hit.
     */
    | { kind: 'unresolved'; eligibleCount: number; disqualification: 'mixed_instrument_basis' | null }
    | {
          kind: 'resolved';
          edgePercent: number;
          coveredCount: number;
          eligibleCount: number;
          /** coveredCount / eligibleCount */
          coverage: number;
          qualifies: boolean;
          disqualification: RelativeEdgeDisqualification | null;
      };

/** The exact report fields the chip reads, so tests and callers need no wider lineage object. */
export interface RelativeEdgeChipInput {
    lineage: Pick<RegimeDependenceProfileReport['lineage'], 'optionEvidence' | 'relativeEdgeEvidence'>;
    thresholds: Pick<RegimeProfileThresholds, 'minRelativeEdgeCoverage' | 'minRelativeEdgeCoveredCount'>;
}

/**
 * Uses the backend-resolved evidence for both option and underlying reports. Mixed instrument basis is
 * judged before zero coverage, matching the backend core's `diagnoseRelativeEdgeEvidence` precedence.
 */
export function getRelativeEdgeChipState(report: RelativeEdgeChipInput): RelativeEdgeChipState {
    const evidence = report.lineage.relativeEdgeEvidence;
    const mixedBasis = evidence.instrumentBasis === 'mixed';

    if (evidence.edgePercent === null) {
        return {
            disqualification: mixedBasis ? 'mixed_instrument_basis' : null,
            eligibleCount: evidence.eligibleCount,
            kind: 'unresolved'
        };
    }

    const coverage = evidence.eligibleCount > 0 ? evidence.coveredCount / evidence.eligibleCount : 0;

    let disqualification: RelativeEdgeDisqualification | null = null;

    if (mixedBasis) {
        disqualification = 'mixed_instrument_basis';
    } else if (evidence.edgePercent <= 0) {
        disqualification = 'not_positive';
    } else if (
        evidence.coveredCount < report.thresholds.minRelativeEdgeCoveredCount ||
        coverage < report.thresholds.minRelativeEdgeCoverage
    ) {
        disqualification = 'coverage_insufficient';
    }

    return {
        coverage,
        coveredCount: evidence.coveredCount,
        disqualification,
        edgePercent: evidence.edgePercent,
        eligibleCount: evidence.eligibleCount,
        kind: 'resolved',
        qualifies: disqualification === null
    };
}

export function getRelativeEdgeDisqualificationLabel(disqualification: RelativeEdgeDisqualification): string {
    switch (disqualification) {
        case 'mixed_instrument_basis':
            return 'mixed option/underlying basis';
        case 'not_positive':
            return 'edge not positive';
        case 'coverage_insufficient':
            return 'coverage below minimum';
    }
}

// ---------------------------------------------------------------------------
// Layer 1b — insufficient-evidence checklist
// ---------------------------------------------------------------------------

export interface EvidenceChecklistRow {
    code: string;
    label: string;
    actualDisplay: string;
    requiredDisplay: string;
    passed: boolean;
    /** Backend-authored reason detail, when the row came from a verdict reason. */
    detail?: string;
}

const CORE_SUFFICIENCY_CODES = new Set([
    'total_trades_below_minimum',
    'primary_dimension_coverage_low',
    'total_primary_episodes_below_minimum',
    'too_few_evaluable_primary_slices'
]);

interface ExtraReasonPresentation {
    label: string;
    /** How `value` / `threshold` on the reason should be formatted. */
    valueKind: 'share' | 'count' | 'percent';
    /** Comparator wording for the requirement column. */
    requirement: 'atLeast' | 'below' | 'positive';
}

const EXTRA_REASON_PRESENTATIONS: Record<string, ExtraReasonPresentation> = {
    episode_starved_slices_dominate: { label: 'Episode-starved slice dominance', requirement: 'below', valueKind: 'share' },
    relative_edge_coverage_insufficient: { label: 'Relative-edge coverage', requirement: 'atLeast', valueKind: 'share' },
    relative_edge_evidence_missing: { label: 'Relative-edge evidence', requirement: 'atLeast', valueKind: 'count' },
    relative_edge_mixed_instrument_basis: { label: 'Relative-edge instrument basis', requirement: 'atLeast', valueKind: 'count' },
    relative_edge_not_positive: { label: 'Relative edge', requirement: 'positive', valueKind: 'percent' },
    relative_edge_source_not_resolved: { label: 'Relative-edge provenance', requirement: 'atLeast', valueKind: 'count' },
    relative_edge_unresolved_no_coverage: { label: 'Relative-edge baseline coverage', requirement: 'atLeast', valueKind: 'count' }
};

function formatReasonValue(value: number | undefined, kind: ExtraReasonPresentation['valueKind']): string {
    if (value === undefined) return '—';

    switch (kind) {
        case 'share':
            return formatShare(value, 1);
        case 'count':
            return formatInt(value);
        case 'percent':
            return formatSignedPercent(value, 3);
    }
}

function formatReasonRequirement(threshold: number | undefined, presentation: ExtraReasonPresentation): string {
    if (presentation.requirement === 'positive') return 'need > 0%';
    if (threshold === undefined) return '—';

    const formatted = presentation.valueKind === 'share' ? formatShare(threshold) : formatInt(threshold);

    return presentation.requirement === 'below' ? `need < ${formatted}` : `need ≥ ${formatted}`;
}

/** Unknown-code fallback: units are unknown, so render the raw number rather than guessing a format. */
function formatUnknownReasonNumber(value: number | undefined): string {
    if (value === undefined) return '—';

    return Number.isInteger(value) ? formatInt(value) : String(Number(value.toFixed(4)));
}

export function getPrimaryDimension(report: Pick<RegimeDependenceProfileReport, 'dimensions'>): RegimeProfileDimensionResult {
    const primary = report.dimensions.find((dimension) => dimension.role === 'primary');

    if (!primary) {
        throw new Error('Regime profile report has no primary dimension.');
    }

    return primary;
}

export function getTotalPrimaryEpisodes(primary: RegimeProfileDimensionResult): number {
    return primary.slices.reduce((sum, slice) => sum + slice.episodeCount, 0);
}

function countEvaluableSlices(primary: RegimeProfileDimensionResult, thresholds: RegimeProfileThresholds): number {
    return primary.slices.filter(
        (slice) => slice.tradeCount >= thresholds.minTradesPerSlice && slice.episodeCount >= thresholds.minEpisodesPerSlice
    ).length;
}

/**
 * Recomputes the four sufficiency gates from the report exactly as the backend core derives them, so the
 * checklist can show passing rows too (the report only records the failing ones), then appends every
 * additional failing reason from the mapped families.
 */
export function buildInsufficientEvidenceChecklist(
    report: Pick<RegimeDependenceProfileReport, 'dimensions' | 'thresholds' | 'verdict'>
): EvidenceChecklistRow[] {
    const primary = getPrimaryDimension(report);
    const { thresholds } = report;

    const totalTrades = primary.classifiedTradeCount + primary.unclassifiedTradeCount;
    const unclassifiedShare = totalTrades > 0 ? primary.unclassifiedTradeCount / totalTrades : 1;
    const totalEpisodes = getTotalPrimaryEpisodes(primary);
    const evaluableSliceCount = countEvaluableSlices(primary, thresholds);

    const rows: EvidenceChecklistRow[] = [
        {
            actualDisplay: `${formatInt(totalTrades)} trades`,
            code: 'total_trades_below_minimum',
            label: 'Profiled trades',
            passed: totalTrades >= thresholds.minTotalTrades,
            requiredDisplay: `need ≥ ${formatInt(thresholds.minTotalTrades)}`
        },
        {
            actualDisplay: `${formatShare(unclassifiedShare, 1)} unclassified`,
            code: 'primary_dimension_coverage_low',
            label: 'Point-in-time classification',
            passed: unclassifiedShare <= thresholds.maxPrimaryUnclassifiedShare,
            requiredDisplay: `need ≤ ${formatShare(thresholds.maxPrimaryUnclassifiedShare)}`
        },
        {
            actualDisplay: `${formatInt(totalEpisodes)} episodes`,
            code: 'total_primary_episodes_below_minimum',
            label: 'Independent primary episodes',
            passed: totalEpisodes >= thresholds.minTotalPrimaryEpisodes,
            requiredDisplay: `need ≥ ${formatInt(thresholds.minTotalPrimaryEpisodes)}`
        },
        {
            actualDisplay: `${formatInt(evaluableSliceCount)} ${evaluableSliceCount === 1 ? 'slice' : 'slices'}`,
            code: 'too_few_evaluable_primary_slices',
            label: 'Evaluable primary states',
            passed: evaluableSliceCount >= thresholds.minEvaluablePrimarySlices,
            requiredDisplay: `need ≥ ${formatInt(thresholds.minEvaluablePrimarySlices)}`
        }
    ];

    for (const reason of report.verdict.reasons) {
        if (CORE_SUFFICIENCY_CODES.has(reason.code)) continue;

        const presentation = EXTRA_REASON_PRESENTATIONS[reason.code];

        if (presentation) {
            rows.push({
                actualDisplay: formatReasonValue(reason.value, presentation.valueKind),
                code: reason.code,
                detail: reason.detail,
                label: presentation.label,
                passed: false,
                requiredDisplay: formatReasonRequirement(reason.threshold, presentation)
            });
            continue;
        }

        // A reason code this build does not know (newer backend) must never vanish: an all-green
        // checklist under an "insufficient evidence" heading would be a lie. Render it raw.
        rows.push({
            actualDisplay: formatUnknownReasonNumber(reason.value),
            code: reason.code,
            detail: reason.detail,
            label: reason.code,
            passed: false,
            requiredDisplay: reason.threshold !== undefined ? `need ${formatUnknownReasonNumber(reason.threshold)}` : '—'
        });
    }

    return rows;
}

// ---------------------------------------------------------------------------
// Layer 2 — primary panel rows
// ---------------------------------------------------------------------------

export interface PrimarySliceRow extends RegimeProfileSliceMetrics {
    /** tradeCount below minTradesPerSlice: greyed with "below evaluation minimum", never dropped. */
    belowEvaluationMinimum: boolean;
}

/** Sorted by winsorized mean profit ascending so adverse states surface at the top edge. */
export function buildPrimarySliceRows(dimension: RegimeProfileDimensionResult, thresholds: RegimeProfileThresholds): PrimarySliceRow[] {
    return dimension.slices
        .map((slice) => ({
            ...slice,
            belowEvaluationMinimum: slice.tradeCount < thresholds.minTradesPerSlice
        }))
        .sort((a, b) => a.meanProfitPercent - b.meanProfitPercent || a.state.localeCompare(b.state));
}

export interface ConcentrationFlag {
    key: 'entity' | 'episode' | 'date';
    label: string;
    value: number | null;
    threshold: number;
    flagged: boolean;
}

/** The three concentration shares, each against its own fragile threshold from the report. */
export function getConcentrationFlags(slice: RegimeProfileSliceMetrics, thresholds: RegimeProfileThresholds): ConcentrationFlag[] {
    const build = (key: ConcentrationFlag['key'], label: string, value: number | null, threshold: number): ConcentrationFlag => ({
        flagged: value !== null && value >= threshold,
        key,
        label,
        threshold,
        value
    });

    return [
        build('entity', 'Top entity profit share', slice.topEntityProfitShare, thresholds.fragileTopEntityProfitShare),
        build('episode', 'Top episode profit share', slice.topEpisodeProfitShare, thresholds.fragileTopEpisodeProfitShare),
        build('date', 'Top date profit share', slice.topDateProfitShare, thresholds.fragileTopDateProfitShare)
    ];
}

export interface DivergingBarSegment {
    leftPercent: number;
    widthPercent: number;
}

export interface DivergingBarScale {
    /** Horizontal position of the zero axis, as a percentage of the track width. */
    zeroPercent: number;
    segmentFor: (value: number) => DivergingBarSegment;
}

/**
 * Shared-scale diverging horizontal bar layout: one zero axis for every row, negative values extending
 * left and positive right, each proportional to the full observed range.
 */
export function buildDivergingBarScale(values: readonly number[]): DivergingBarScale {
    let negativeMax = 0;
    let positiveMax = 0;

    for (const value of values) {
        if (value < 0) negativeMax = Math.max(negativeMax, -value);
        else positiveMax = Math.max(positiveMax, value);
    }

    const total = negativeMax + positiveMax;

    if (total === 0) {
        return { segmentFor: () => ({ leftPercent: 50, widthPercent: 0 }), zeroPercent: 50 };
    }

    const zeroPercent = (negativeMax / total) * 100;

    return {
        segmentFor: (value: number): DivergingBarSegment => {
            const widthPercent = (Math.abs(value) / total) * 100;

            return value < 0 ? { leftPercent: zeroPercent - widthPercent, widthPercent } : { leftPercent: zeroPercent, widthPercent };
        },
        zeroPercent
    };
}

// ---------------------------------------------------------------------------
// Layer 4 — dimension grouping and interaction matrices
// ---------------------------------------------------------------------------

export const REGIME_DIMENSION_LABELS: Record<RegimeProfileDimensionKey, string> = {
    'entity.marketAlignmentBucket': 'Entity market alignment',
    'entity.trendState': 'Entity trend',
    'interaction.marketComposite_x_entityTrend': 'Market composite × entity trend',
    'interaction.marketComposite_x_relativeLeadership': 'Market composite × relative leadership',
    'market.breadthState': 'Market breadth',
    'market.compositeState': 'Market composite state',
    'market.trendState': 'Market trend',
    'market.volatilityState': 'Market volatility',
    'oneil.marketDirectionState': "O'Neil market direction",
    'relative.betaAdjustedLeadershipState': 'Relative leadership (beta-adjusted)'
};

export interface GroupedRegimeDimensions {
    primary: RegimeProfileDimensionResult;
    drillDown: RegimeProfileDimensionResult[];
    independent: RegimeProfileDimensionResult[];
    contextual: RegimeProfileDimensionResult[];
    interactions: RegimeProfileDimensionResult[];
}

export function groupDimensionsByRole(dimensions: readonly RegimeProfileDimensionResult[]): GroupedRegimeDimensions {
    const primary = dimensions.find((dimension) => dimension.role === 'primary');

    if (!primary) {
        throw new Error('Regime profile report has no primary dimension.');
    }

    return {
        contextual: dimensions.filter((dimension) => dimension.role === 'contextual'),
        drillDown: dimensions.filter((dimension) => dimension.role === 'drillDown'),
        independent: dimensions.filter((dimension) => dimension.role === 'independent'),
        interactions: dimensions.filter((dimension) => dimension.role === 'interaction'),
        primary
    };
}

const INTERACTION_STATE_SEPARATOR = '|';

export interface InteractionMatrix {
    /** Market composite states (matrix rows), sorted ascending. */
    rowStates: string[];
    /** Secondary dimension states (matrix columns), sorted ascending. */
    columnStates: string[];
    /** Keyed by `${rowState}|${columnState}`. */
    cellsByKey: Map<string, RegimeProfileSliceMetrics>;
    /** Slice states that did not parse as a `market|secondary` pair; rendered as a visible fallback list. */
    unparseableStates: string[];
}

export function buildInteractionMatrix(dimension: RegimeProfileDimensionResult): InteractionMatrix {
    const rowStates = new Set<string>();
    const columnStates = new Set<string>();
    const cellsByKey = new Map<string, RegimeProfileSliceMetrics>();
    const unparseableStates: string[] = [];

    for (const slice of dimension.slices) {
        const parts = slice.state.split(INTERACTION_STATE_SEPARATOR);

        if (parts.length !== 2 || parts[0] === '' || parts[1] === '') {
            unparseableStates.push(slice.state);
            continue;
        }

        const [rowState, columnState] = parts as [string, string];

        rowStates.add(rowState);
        columnStates.add(columnState);
        cellsByKey.set(slice.state, slice);
    }

    return {
        cellsByKey,
        columnStates: Array.from(columnStates).sort((a, b) => a.localeCompare(b)),
        rowStates: Array.from(rowStates).sort((a, b) => a.localeCompare(b)),
        unparseableStates
    };
}

// ---------------------------------------------------------------------------
// Main-leg report scope selection and run polling
// ---------------------------------------------------------------------------

export interface MainLegReportSelection {
    summary: RegimeDependenceProfileReportSummary | null;
}

/** Show the newest report for the selected outcome scope, without preferring a workflow stage. */
export function selectMainLegReportSummary(
    summaries: readonly RegimeDependenceProfileReportSummary[],
    experimentId: number,
    scope?: {
        outcomeMode: 'fixedHorizon' | 'realizedStrategyExit';
        outcomeBasis: string | null;
        horizonSeconds: number | null;
        realizedOutcomeSetId: number | null;
    }
): MainLegReportSelection {
    return {
        summary:
            summaries.find(
                (summary) =>
                    summary.experimentId === experimentId &&
                    (!scope ||
                        (summary.outcomeMode === scope.outcomeMode &&
                            (scope.outcomeMode === 'realizedStrategyExit'
                                ? summary.realizedOutcomeSetId === scope.realizedOutcomeSetId
                                : summary.outcomeBasis === scope.outcomeBasis && summary.horizonSeconds === scope.horizonSeconds)))
            ) ?? null
    };
}

export const REGIME_RUN_POLL_INTERVAL_MS = 3000;

/**
 * Poll cadence for `regimeProfile.getRun`. A query in error state must stop polling — otherwise a dead
 * or unreachable job is re-polled forever — and terminal job states stop it too. An in-flight first
 * fetch (no status yet, no error) keeps polling.
 */
export function getRunPollIntervalMs(params: {
    jobStatus: RegimeDependenceProfileJob['status'] | undefined;
    isError: boolean;
}): number | false {
    if (params.isError) return false;
    if (params.jobStatus === 'completed' || params.jobStatus === 'failed') return false;

    return REGIME_RUN_POLL_INTERVAL_MS;
}

// ---------------------------------------------------------------------------
// Design rule 6 — stale-lineage detection
// ---------------------------------------------------------------------------

export type RegimePinScope = 'market' | 'entity' | 'relative';

export interface RegimeLineageMismatch {
    scope: RegimePinScope;
    resolvedVersionId: number;
    /** What a run started now would resolve for this scope. */
    currentVersionId: number;
    /** Where the current version came from: the strategy's 1d pin, or the active model. */
    basis: 'pin' | 'active';
}

export type RegimeLineageStaleness =
    /** No comparison basis on any scope (no pin and no known active): undecidable, never falsely consistent. */
    | { status: 'unknown' }
    | { status: 'consistent'; comparedScopes: RegimePinScope[] }
    | { status: 'stale'; comparedScopes: RegimePinScope[]; mismatches: RegimeLineageMismatch[] };

const PIN_SCOPES: readonly RegimePinScope[] = ['market', 'entity', 'relative'];

/**
 * Compares the report's resolved 1d model versions against what a run started now would resolve: the
 * strategy's 1d pin for a scope when one exists (pins take precedence — resolution order is
 * input > strategy > active), otherwise the current active model version from
 * `listForStrategy.activeModelVersionIds`. A scope with no pin and a null active id is undecidable and
 * is skipped; a report with zero decidable scopes reads as `unknown`, never as falsely consistent.
 *
 * The O'Neil direction scope is deliberately not judged here: it has no strategy pin, and profiling an
 * older training window legitimately passes a retired backfilled version (plan open question 3), so an
 * active-version mismatch there is expected rather than evidence of staleness.
 */
export function evaluateRegimeLineageStaleness(params: {
    resolvedModelVersionIds: RegimeDependenceProfileReport['lineage']['resolvedModelVersionIds'];
    currentStrategyPins: RegimeModelVersionOverrides | null | undefined;
    /** Current active 1d model versions from listForStrategy; undefined while the list has not loaded. */
    activeModelVersionIds?: RegimeProfileActiveModelVersionIds | undefined;
}): RegimeLineageStaleness {
    const pins = params.currentStrategyPins;
    const comparedScopes: RegimePinScope[] = [];
    const mismatches: RegimeLineageMismatch[] = [];

    for (const scope of PIN_SCOPES) {
        const pinnedVersionId = pins?.[scope]?.['1d'];
        const activeVersionId = params.activeModelVersionIds?.[scope] ?? null;

        let currentVersionId: number;
        let basis: RegimeLineageMismatch['basis'];

        if (pinnedVersionId !== undefined) {
            currentVersionId = pinnedVersionId;
            basis = 'pin';
        } else if (activeVersionId !== null) {
            currentVersionId = activeVersionId;
            basis = 'active';
        } else {
            continue;
        }

        comparedScopes.push(scope);

        const resolvedVersionId = params.resolvedModelVersionIds[scope];

        if (currentVersionId !== resolvedVersionId) {
            mismatches.push({ basis, currentVersionId, resolvedVersionId, scope });
        }
    }

    if (comparedScopes.length === 0) return { status: 'unknown' };
    if (mismatches.length > 0) return { comparedScopes, mismatches, status: 'stale' };

    return { comparedScopes, status: 'consistent' };
}
